import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import {
  useGetAnonymousJwtTokenApiV1UsersAnonymousPost,
  useGetCurrentUserApiV1UsersSelfGet,
  useGetProfileApiV1UsersSelfProfileGet
} from 'api/endpoints/openapi-generated'
import {
  HTTPValidationError,
  ProfileResource,
  UserResource,
  UserResponse
} from 'api/endpoints/openapi-generated.schemas'
import { ANONYMOUS_USER_TOKEN_LS_KEY } from 'constants/ls'
import { useIdentify } from 'hooks/use-analytics'
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react'
import { createEvent } from 'effector'
import { restore } from 'effector/effector.umd'
import { useUnit } from 'effector-react'

export type User = UserResource & {
  name?: string
  isOnboardingPassed?: boolean
}

interface AuthContextType {
  user: User | null
  userProfile: ProfileResource | null
  isAuthorized: boolean
  isAnonymous: boolean
  setUser: (user: User | null) => void
  setUserProfile: (profile: ProfileResource | null) => void
  setIsAnonymous: (isAnonymous: boolean) => void
}

export const gotUser = createEvent<User | null>()
export const $user = restore(gotUser, null)

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const user = useUnit($user)
  const [userProfile, setUserProfile] = useState<ProfileResource | null>(null)
  const [, setIsAnonymous] = useState<boolean>(false)
  const { identify } = useIdentify()

  useEffect(() => {
    if (user?.id) {
      identify(user.id)
    }
  }, [identify, user?.id])

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        isAuthorized: Boolean(user?.id),
        isAnonymous: Boolean(user?.is_anonymous),
        setIsAnonymous,
        setUserProfile,
        setUser: gotUser
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export type UseAuthReturnType = AuthContextType & {
  refetchUser: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<UserResponse, void | HTTPValidationError>>
}

export const useAuth = (): UseAuthReturnType => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }

  const [accessToken, setAccessToken] = useState(() => {
    return localStorage.getItem(`token`)
  })
  const [tempToken, setTempToken] = useState(() => {
    return localStorage.getItem(ANONYMOUS_USER_TOKEN_LS_KEY)
  })
  const { user, setUser, setUserProfile } = context

  const { mutate: getAnonymousToken } =
    useGetAnonymousJwtTokenApiV1UsersAnonymousPost()

  useEffect(() => {
    if ((!accessToken || !user?.id) && !tempToken) {
      getAnonymousToken((() => {})(), {
        onSuccess: (token) => {
          localStorage.setItem(ANONYMOUS_USER_TOKEN_LS_KEY, token.access_token)
          setTempToken(token.access_token)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, tempToken])

  const {
    data: userData,
    refetch: refetchUserData,
    error: userDataError,
    isError: hasUserDataError
  } = useGetCurrentUserApiV1UsersSelfGet({
    query: { enabled: !!accessToken || !!tempToken, retry: false }
  })

  const {
    data: userProfileData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: refetchUserProfileData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: userProfileDataError
  } = useGetProfileApiV1UsersSelfProfileGet(
    {
      user_id: user?.id || ''
    },
    { query: { enabled: !!user?.id } }
  )

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    if (hasUserDataError && userDataError?.status === 401) {
      if (accessToken) {
        localStorage.removeItem('token')
        setAccessToken('')
        refetchUserData()
        return
      }
      if (tempToken) {
        localStorage.removeItem(ANONYMOUS_USER_TOKEN_LS_KEY)
        setTempToken('')
        refetchUserData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDataError, hasUserDataError, accessToken, tempToken])

  useEffect(() => {
    if (userData?.data.id) {
      setUser(userData?.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.data])

  useEffect(() => {
    if (userProfileData?.data) {
      setUserProfile(userProfileData.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfileData?.data])

  return {
    ...context,
    refetchUser: refetchUserData
  }
}
